import { keys } from '@/app/i18n'
import { EMPLOYER_SOFTWARES } from '@/app/stores/session'
import type { SettingsTabDefinition } from '@/shared/settings/tabs'

/**
 * The settings module's own tabs (wp/settings §1.1, legacy `Settings.vue` `tabSets`): software lists as the legacy
 * arrays (OPEN-6), Members for the Client only, and the API's permissions — a Staff member sees a tab only with its
 * `read` resource and its write controls only with its `write` resource (FX-070, FX-027, FX-032; OPEN-2 (a)). "List
 * open" tabs have no `read`. The three pipeline slugs are spelt correctly; the legacy misspellings redirect (D-15).
 */
const tabs: SettingsTabDefinition[] = [
  {
    slug: 'configuration',
    label: keys.t('settings.shell.tab.configuration'),
    icon: 'SettingsIcon',
    order: 10,
    softwares: EMPLOYER_SOFTWARES,
    component: () => import('./components/tabs/ConfigurationTab.vue'),
  },
  {
    slug: 'members',
    label: keys.t('settings.shell.tab.members'),
    icon: 'UsersIcon',
    order: 20,
    softwares: EMPLOYER_SOFTWARES,
    clientOnly: true,
    component: () => import('./components/tabs/MembersTab.vue'),
  },
  {
    slug: 'message-templates',
    label: keys.t('settings.shell.tab.messageTemplates'),
    icon: 'MessageSquareIcon',
    order: 30,
    softwares: ['re', 'hr'],
    write: (software) => (software === 'hr' ? 'onboarding' : 'applications'),
    component: () => import('./components/tabs/MessageTemplatesTab.vue'),
  },
  {
    slug: 'task-types',
    label: keys.t('settings.shell.tab.taskTypes'),
    icon: 'TrelloIcon',
    order: 40,
    softwares: EMPLOYER_SOFTWARES,
    read: 'tasks',
    write: 'tasks',
    component: () => import('./components/tabs/TaskTypesTab.vue'),
  },
  {
    slug: 'document-types',
    label: keys.t('settings.shell.tab.documentTypes'),
    icon: 'FileIcon',
    order: 50,
    softwares: EMPLOYER_SOFTWARES,
    write: 'documents',
    component: () => import('./components/tabs/DocumentTypesTab.vue'),
  },
  {
    // BR-094: one list for every module; Staff change it with Documents write (or Applications write, API side)
    slug: 'tags',
    label: keys.t('settings.shell.tab.tags'),
    icon: 'TagIcon',
    order: 55,
    softwares: EMPLOYER_SOFTWARES,
    write: 'documents',
    component: () => import('./components/tabs/TagsTab.vue'),
  },
  {
    // MagicText (2026-11-04): the workspace owner's switch for "Write with AI", every software
    slug: 'ai',
    label: keys.t('settings.shell.tab.ai'),
    icon: 'SparklesIcon',
    order: 57,
    softwares: EMPLOYER_SOFTWARES,
    clientOnly: true,
    component: () => import('./components/tabs/AiTab.vue'),
  },
  {
    slug: 'recruiting-pipeline',
    label: keys.t('settings.shell.tab.recruitingPipeline'),
    icon: 'ListIcon',
    order: 60,
    softwares: ['re'],
    write: 'applications',
    component: () => import('./components/tabs/RecruitingPipelineTab.vue'),
  },
  {
    slug: 'onboarding-pipeline',
    label: keys.t('settings.shell.tab.onboardingPipeline'),
    icon: 'ListIcon',
    order: 70,
    softwares: ['hr'],
    read: 'onboarding',
    write: 'onboarding',
    component: () => import('./components/tabs/OnboardingPipelineTab.vue'),
  },
  {
    slug: 'offboarding-pipeline',
    label: keys.t('settings.shell.tab.offboardingPipeline'),
    icon: 'ListIcon',
    order: 80,
    softwares: ['hr'],
    read: 'offboarding',
    write: 'offboarding',
    component: () => import('./components/tabs/OffboardingPipelineTab.vue'),
  },
  {
    slug: 'holidays',
    label: keys.t('settings.shell.tab.holidays'),
    icon: 'CoffeeIcon',
    order: 90,
    softwares: ['hr'],
    read: 'leave_absence',
    write: 'leave_absence',
    component: () => import('./components/tabs/HolidaysTab.vue'),
  },
  {
    slug: 'absence-types',
    label: keys.t('settings.shell.tab.timeOff'),
    icon: 'BellOffIcon',
    order: 100,
    softwares: ['hr'],
    read: 'leave_absence',
    write: 'leave_absence',
    component: () => import('./components/tabs/TimeOffTab.vue'),
  },
  {
    slug: 'score-templates',
    label: keys.t('settings.shell.tab.scoreTemplates'),
    icon: 'ThumbsUpIcon',
    order: 110,
    softwares: ['hr'],
    write: 'onboarding',
    component: () => import('./components/tabs/ScoreTemplatesTab.vue'),
  },
  {
    slug: 'performance-ratings',
    label: keys.t('settings.shell.tab.performanceRatings'),
    icon: 'StarIcon',
    order: 120,
    softwares: ['hr'],
    read: 'onboarding',
    write: 'onboarding',
    component: () => import('./components/tabs/PerformanceRatingsTab.vue'),
  },
  {
    slug: 'payment-providers',
    label: keys.t('settings.shell.tab.paymentProviders'),
    icon: 'CreditCardIcon',
    order: 130,
    softwares: ['ac'],
    read: 'invoices',
    write: 'invoices',
    component: () => import('./components/tabs/PaymentProvidersTab.vue'),
  },
  {
    slug: 'job-levels',
    label: keys.t('settings.shell.tab.jobLevels'),
    icon: 'UserCheckIcon',
    order: 200,
    softwares: ['hr'],
    write: 'employees',
    component: () => import('./components/tabs/JobLevelsTab.vue'),
  },
]

export default tabs
